import { Button } from "@/components/ui/button";
import { Briefcase, Trash2, Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Toast from "@/pages/portal/components/Toast";

interface Country {
    label: string;
    value: string;
}

interface JobType {
    label: string;
    value: string;
}

interface ExperienceModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    editingItem: any;
    onDelete: () => void;
    onSave: (data: any) => Promise<void> | void;
    countries?: Country[];
    jobTypes?: JobType[];
}

export default function ExperienceModal({
    isOpen,
    onOpenChange,
    editingItem,
    onDelete,
    onSave,
    countries = [],
    jobTypes = [],
}: ExperienceModalProps) {
    const months = [
        { label: "January", value: "01" },
        { label: "February", value: "02" },
        { label: "March", value: "03" },
        { label: "April", value: "04" },
        { label: "May", value: "05" },
        { label: "June", value: "06" },
        { label: "July", value: "07" },
        { label: "August", value: "08" },
        { label: "September", value: "09" },
        { label: "October", value: "10" },
        { label: "November", value: "11" },
        { label: "December", value: "12" },
    ];
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: (currentYear + 2) - (currentYear - 50) + 1 }, (_, i) => {
        const year = (currentYear + 2 - i).toString();
        return { label: year, value: year };
    });

    // Local state for new fields
    const [jobType, setJobType] = React.useState(editingItem?.jobType || "");
    const [country, setCountry] = React.useState(editingItem?.country || "");
    const [city, setCity] = React.useState(editingItem?.city || "");
    const [openCountryPicker, setOpenCountryPicker] = React.useState(false);
    const [role, setRole] = React.useState(editingItem?.role || "");
    const [company, setCompany] = React.useState(editingItem?.company || "");
    const [currentlyWorking, setCurrentlyWorking] = React.useState(editingItem?.currentlyWorking || false);
    const [beginMonth, setBeginMonth] = React.useState(editingItem?.beginMonth || "");
    const [beginYear, setBeginYear] = React.useState(editingItem?.beginYear || "");
    const [endMonth, setEndMonth] = React.useState(editingItem?.endMonth || "");
    const [endYear, setEndYear] = React.useState(editingItem?.endYear || "");
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = React.useState(false);
    const [showValidationToast, setShowValidationToast] = React.useState(false);
    const scrollableRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (isOpen) {
            setRole(editingItem?.role || "");
            setCompany(editingItem?.company || "");
            setJobType(editingItem?.jobType || "");
            setCountry(editingItem?.country || "");
            setCity(editingItem?.city || "");
            setCurrentlyWorking(editingItem?.currentlyWorking || false);
            setBeginMonth(editingItem?.beginMonth || "");
            setBeginYear(editingItem?.beginYear || "");
            setEndMonth(editingItem?.endMonth || "");
            setEndYear(editingItem?.endYear || "");
            setErrors({});
            setShowValidationToast(false);
            setIsSaving(false);
        }
    }, [isOpen, editingItem]);

    const handleSave = async () => {
        const newErrors: Record<string, string> = {};
        if (!role.trim()) {
            newErrors.role = "Job title is required.";
        }
        if (!company.trim()) {
            newErrors.company = "Company is required.";
        }
        if (!jobType) {
            newErrors.jobType = "Job type is required.";
        }
        if (!country) {
            newErrors.country = "Country is required.";
        }
        if (!beginMonth) {
            newErrors.beginMonth = "Begin month is required.";
        }
        if (!beginYear) {
            newErrors.beginYear = "Begin year is required.";
        }
        if (!currentlyWorking) {
            if (!endMonth) {
                newErrors.endMonth = "End month is required.";
            }
            if (!endYear) {
                newErrors.endYear = "End year is required.";
            }
            // Validate end year is not before start year
            if (beginYear && endYear) {
                const startYearNum = parseInt(beginYear, 10);
                const endYearNum = parseInt(endYear, 10);
                if (endYearNum < startYearNum) {
                    newErrors.endYear = "End year cannot be before begin year.";
                }
            }
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setShowValidationToast(true);
            const fieldOrder = ["role", "company", "jobType", "country", "beginMonth", "beginYear", "endMonth", "endYear"];
            const firstErrorField = fieldOrder.find((f) => newErrors[f]);
            if (firstErrorField) {
                setTimeout(() => {
                    document.getElementById(`exp-field-${firstErrorField}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }, 0);
            }
            return;
        }
        setIsSaving(true);
        try {
            await onSave({
                id: editingItem?.id,
                role,
                company,
                jobType,
                country,
                city,
                currentlyWorking,
                beginMonth,
                beginYear,
                endMonth: currentlyWorking ? "" : endMonth,
                endYear: currentlyWorking ? "" : endYear,
                duration: beginYear && (currentlyWorking ? "Present" : endYear)
                    ? `${beginYear} – ${currentlyWorking ? "Present" : endYear}`
                    : "",
            });
            setErrors({});
        } catch (error: any) {
            const msg = error?.response?.data?.detail || error?.message || "Failed to save. Please try again.";
            throw new Error(msg);
        } finally {
            setIsSaving(false);
        }
    };

    const fieldCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus-visible:ring-2 focus-visible:ring-[#001B44]/15 focus-visible:border-[#001B44] transition-colors ${
            error ? "border-red-400 focus-visible:ring-red-200/60" : "border-slate-800"
        }`;
    const triggerCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus:ring-2 focus:ring-[#001B44]/15 focus:border-[#001B44] transition-colors ${
            error ? "border-red-400" : "border-slate-800"
        }`;

    const SectionHeading = ({ children }: { children: React.ReactNode }) => (
        <div className="flex items-center gap-3 pb-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                {children}
            </span>
            <div className="flex-1 h-px bg-slate-100" />
        </div>
    );

    const FieldLabel = ({ htmlFor, children, required }: { htmlFor?: string; children: React.ReactNode; required?: boolean }) => (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1.5">
            {children}
            {required && <span className="text-rose-400 text-xs ml-1">*</span>}
        </label>
    );

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] rounded-2xl p-0 border-none shadow-2xl flex flex-col bg-white overflow-hidden">
                {showValidationToast && (
                    <Toast
                        message="Please fill in all required fields."
                        type="error"
                        mode="absolute"
                        onClose={() => setShowValidationToast(false)}
                        duration={4000}
                    />
                )}

                {/* ── Header ───────────────────────────────────────────── */}
                <DialogHeader className="px-6 pt-6 pb-5 shrink-0 border-b border-slate-100">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#001B44]/[0.06] flex items-center justify-center text-[#001B44] shrink-0">
                            <Briefcase className="w-5 h-5" />
                        </div>
                        <div>
                            <DialogTitle className="text-[17px] font-bold text-slate-900 leading-tight">
                                {editingItem ? "Edit Experience" : "Add Experience"}
                            </DialogTitle>
                            <p className="text-sm text-slate-400 mt-0.5">
                                {editingItem
                                    ? "Update your work experience details."
                                    : "Add a job or work experience to your profile."}
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                {/* ── Scrollable body ───────────────────────────────────── */}
                <div ref={scrollableRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-7">

                    {/* Section 1 — Position Details */}
                    <div className="space-y-4">
                        <SectionHeading>Position Details</SectionHeading>

                        <div id="exp-field-role">
                            <FieldLabel required>Job Title</FieldLabel>
                            <Input
                                value={role}
                                onChange={(e) => { setRole(e.target.value); setErrors((p) => ({ ...p, role: "" })); }}
                                placeholder="e.g. Software Engineer"
                                className={fieldCls(errors.role)}
                            />
                            {errors.role && <p className="text-xs text-red-500 mt-1">{errors.role}</p>}
                        </div>

                        <div id="exp-field-company">
                            <FieldLabel required>Company / Organization</FieldLabel>
                            <Input
                                value={company}
                                onChange={(e) => { setCompany(e.target.value); setErrors((p) => ({ ...p, company: "" })); }}
                                placeholder="Enter company name"
                                className={fieldCls(errors.company)}
                            />
                            {errors.company && <p className="text-xs text-red-500 mt-1">{errors.company}</p>}
                        </div>

                        <div id="exp-field-jobType">
                            <FieldLabel required>Job Type</FieldLabel>
                            <Select value={jobType} onValueChange={(v) => { setJobType(v); setErrors((p) => ({ ...p, jobType: "" })); }}>
                                <SelectTrigger className={triggerCls(errors.jobType)}>
                                    <SelectValue placeholder="Select job type" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                    {jobTypes.map((type) => (
                                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.jobType && <p className="text-xs text-red-500 mt-1">{errors.jobType}</p>}
                        </div>
                    </div>

                    {/* Section 2 — Location */}
                    <div className="space-y-4">
                        <SectionHeading>Location</SectionHeading>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <FieldLabel>City</FieldLabel>
                                <Input
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="e.g. Istanbul"
                                    className={fieldCls()}
                                />
                            </div>
                            <div id="exp-field-country">
                                <FieldLabel required>Country</FieldLabel>
                                <Popover open={openCountryPicker} onOpenChange={setOpenCountryPicker}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={`w-full h-11 justify-between rounded-lg border bg-white text-sm font-normal transition-colors focus:ring-2 focus:ring-[#001B44]/15 focus:border-[#001B44] ${
                                                errors.country ? "border-red-400" : "border-slate-800"
                                            } ${!country ? "text-slate-400" : "text-slate-900"}`}
                                        >
                                            {country
                                                ? countries.find((c) => c.value === country)?.label || country
                                                : "Select country…"}
                                            <Search className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-[--radix-popover-trigger-width] min-w-[200px] p-0 z-[150]"
                                        align="start"
                                        side="bottom"
                                        avoidCollisions={true}
                                        onWheel={(event) => event.stopPropagation()}
                                    >
                                        <Command>
                                            <CommandInput placeholder="Search country..." />
                                            <CommandList
                                                className="max-h-[200px] overflow-y-auto overscroll-contain"
                                                onWheel={(event) => {
                                                    const target = event.currentTarget;
                                                    target.scrollTop += event.deltaY;
                                                    event.preventDefault();
                                                }}
                                            >
                                                <CommandEmpty>No country found.</CommandEmpty>
                                                <CommandGroup>
                                                    {countries.map((c) => (
                                                        <CommandItem
                                                            key={c.value}
                                                            value={c.value}
                                                            onSelect={(value) => {
                                                                setCountry(value);
                                                                setOpenCountryPicker(false);
                                                                setErrors((p) => ({ ...p, country: "" }));
                                                            }}
                                                        >
                                                            <Check className={cn("mr-2 h-4 w-4", country === c.value ? "opacity-100" : "opacity-0")} />
                                                            {c.label}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {errors.country && <p className="text-xs text-red-500 mt-1">{errors.country}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Section 3 — Employment Period */}
                    <div className="space-y-4">
                        <SectionHeading>Employment Period</SectionHeading>

                        <div className="flex items-center gap-2">
                            <input
                                id="currentlyWorking"
                                type="checkbox"
                                checked={currentlyWorking}
                                onChange={(e) => setCurrentlyWorking(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-[#001B44] focus:ring-[#001B44]"
                            />
                            <label htmlFor="currentlyWorking" className="text-sm font-medium text-slate-700">
                                I am currently working here
                            </label>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div id="exp-field-beginMonth">
                                <FieldLabel required>Begin Month</FieldLabel>
                                <Select value={beginMonth} onValueChange={(v) => { setBeginMonth(v); setErrors((p) => ({ ...p, beginMonth: "" })); }}>
                                    <SelectTrigger className={triggerCls(errors.beginMonth)}>
                                        <SelectValue placeholder="Month" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {months.map((m) => (
                                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.beginMonth && <p className="text-xs text-red-500 mt-1">{errors.beginMonth}</p>}
                            </div>
                            <div id="exp-field-beginYear">
                                <FieldLabel required>Begin Year</FieldLabel>
                                <Select value={beginYear} onValueChange={(v) => { setBeginYear(v); setErrors((p) => ({ ...p, beginYear: "" })); }}>
                                    <SelectTrigger className={triggerCls(errors.beginYear)}>
                                        <SelectValue placeholder="Year" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {years.map((y) => (
                                            <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.beginYear && <p className="text-xs text-red-500 mt-1">{errors.beginYear}</p>}
                            </div>
                        </div>

                        {!currentlyWorking && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div id="exp-field-endMonth">
                                    <FieldLabel required>End Month</FieldLabel>
                                    <Select value={endMonth} onValueChange={(v) => { setEndMonth(v); setErrors((p) => ({ ...p, endMonth: "" })); }}>
                                        <SelectTrigger className={triggerCls(errors.endMonth)}>
                                            <SelectValue placeholder="Month" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px]">
                                            {months.map((m) => (
                                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.endMonth && <p className="text-xs text-red-500 mt-1">{errors.endMonth}</p>}
                                </div>
                                <div id="exp-field-endYear">
                                    <FieldLabel required>End Year</FieldLabel>
                                    <Select value={endYear} onValueChange={(v) => { setEndYear(v); setErrors((p) => ({ ...p, endYear: "" })); }}>
                                        <SelectTrigger className={triggerCls(errors.endYear)}>
                                            <SelectValue placeholder="Year" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px]">
                                            {years.map((y) => (
                                                <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.endYear && <p className="text-xs text-red-500 mt-1">{errors.endYear}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer ───────────────────────────────────────────── */}
                <DialogFooter className="px-6 pt-4 pb-5 bg-white border-t border-slate-100 shrink-0">
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="shrink-0">
                            {editingItem && (
                                <Button
                                    variant="ghost"
                                    onClick={onDelete}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 font-medium px-3 h-10 text-sm"
                                >
                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={isSaving}
                                className="text-slate-500 hover:text-slate-700 font-medium h-10 text-sm px-4"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="rounded-xl bg-[#001B44] hover:bg-[#002a6e] text-white font-semibold px-6 h-10 text-sm shadow-sm transition-colors"
                            >
                                {isSaving ? "Saving…" : "Save Experience"}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
